import dayjs from 'dayjs';

import type {
    AppVersionChannel,
    AppVersionChannelConfig,
    AppVersionContents,
    AppVersionUpdateItem,
    AppVersionUpdateListParams,
    AppVersionUpdatePayload,
    AppVersionUpdateType,
} from '@/services/app-version-updates';

import type {
    AppVersionFormValues,
    ChannelKey,
    I18nLocale,
    ListSearchFilters,
    TimePreset,
} from './types';
import { CHANNEL_KEYS, I18N_LOCALES } from './types';

export const VERSION_PATTERN = /^\d+\.\d+\.\d+$/;

export const defaultChannelValue = (enabled = false) => ({
    enabled,
    version_name: '',
    download_url: '',
});

export const defaultFormValues = (): AppVersionFormValues => ({
    type: 'Remind',
    ios: defaultChannelValue(false),
    android: defaultChannelValue(false),
    apk: defaultChannelValue(false),
    contents: {
        zh: '',
        en: '',
        ja: '',
        es: '',
        vi: '',
        tr: '',
    },
});

export const recordToFormValues = (record: AppVersionUpdateItem): AppVersionFormValues => ({
    type: record.type ?? 'Remind',
    ios: {
        enabled: Boolean(record.ios_version_name && record.ios_download_url),
        version_name: record.ios_version_name ?? '',
        download_url: record.ios_download_url ?? '',
    },
    android: {
        enabled: Boolean(record.android_version_name && record.android_download_url),
        version_name: record.android_version_name ?? '',
        download_url: record.android_download_url ?? '',
    },
    apk: {
        enabled: Boolean(record.apk_version_name && record.apk_download_url),
        version_name: record.apk_version_name ?? '',
        download_url: record.apk_download_url ?? '',
    },
    contents: {
        zh: record.contents?.zh ?? '',
        en: record.contents?.en ?? '',
        ja: record.contents?.ja ?? '',
        es: record.contents?.es ?? '',
        vi: record.contents?.vi ?? '',
        tr: record.contents?.tr ?? '',
    },
});

const buildChannelPayload = (
    channel: ChannelKey,
    values: AppVersionFormValues,
): AppVersionChannelConfig | undefined => {
    const item = values[channel];
    if (!item.enabled) {
        return undefined;
    }
    return {
        version_name: item.version_name.trim(),
        download_url: item.download_url.trim(),
    };
};

export const formValuesToPayload = (values: AppVersionFormValues): AppVersionUpdatePayload => {
    const contents: AppVersionContents = {
        zh: values.contents.zh.trim(),
        en: values.contents.en.trim(),
    };

    I18N_LOCALES.forEach((locale) => {
        if (locale === 'zh' || locale === 'en') return;
        const text = values.contents[locale]?.trim();
        if (text) {
            contents[locale] = text;
        }
    });

    const payload: AppVersionUpdatePayload = {
        type: values.type,
        contents,
    };

    CHANNEL_KEYS.forEach((channel) => {
        const channelPayload = buildChannelPayload(channel, values);
        if (channelPayload) {
            payload[channel] = channelPayload;
        }
    });

    return payload;
};

export const validateFormValues = (
    values: AppVersionFormValues,
    formatMessage: (descriptor: { id: string }) => string,
): string | null => {
    const enabledChannels = CHANNEL_KEYS.filter((key) => values[key].enabled);
    if (enabledChannels.length === 0) {
        return formatMessage({ id: 'appVersionUpdates.validation.channelRequired' });
    }

    for (const key of enabledChannels) {
        const channel = values[key];
        if (!channel.version_name.trim()) {
            return formatMessage({ id: `appVersionUpdates.validation.${key}VersionRequired` });
        }
        if (!VERSION_PATTERN.test(channel.version_name.trim())) {
            return formatMessage({ id: `appVersionUpdates.validation.${key}VersionFormat` });
        }
        if (!channel.download_url.trim()) {
            return formatMessage({ id: `appVersionUpdates.validation.${key}UrlRequired` });
        }
    }

    if (!values.contents.zh.trim()) {
        return formatMessage({ id: 'appVersionUpdates.validation.zhRequired' });
    }
    if (!values.contents.en.trim()) {
        return formatMessage({ id: 'appVersionUpdates.validation.enRequired' });
    }

    return null;
};

export const resolveCreatedAtRange = (
    preset: TimePreset,
    customRange?: [string, string],
): [string, string] | undefined => {
    if (preset === 'all') return undefined;
    if (preset === 'custom' && customRange?.[0] && customRange?.[1]) {
        return customRange;
    }
    const days = preset === '7d' ? 7 : preset === '30d' ? 30 : 0;
    if (!days) return undefined;
    const end = dayjs().endOf('day');
    const start = dayjs().subtract(days - 1, 'day').startOf('day');
    return [start.toISOString(), end.toISOString()];
};

export const buildListParams = (
    filters: ListSearchFilters,
    page: number,
    size: number,
): AppVersionUpdateListParams => {
    const params: AppVersionUpdateListParams = { page, size };
    if (filters.type) params.type = filters.type;
    if (filters.channel) params.channel = filters.channel as AppVersionChannel;
    if (filters.status) params.status = filters.status;
    const createdAt = resolveCreatedAtRange(filters.timePreset ?? 'all', filters.created_at_range);
    if (createdAt) params.created_at = createdAt;
    return params;
};

export const truncateText = (text?: string, max = 48) => {
    const value = text?.trim() ?? '';
    if (value.length <= max) return value;
    return `${value.slice(0, max)}…`;
};

export const getUpdateTypeColor = (type?: AppVersionUpdateType) => {
    if (type === 'Force') return 'error';
    if (type === 'RemindOnly') return 'warning';
    return 'processing';
};

export const getStatusColor = (status?: string) => {
    if (status === 'Enabled') return 'success';
    if (status === 'Disabled') return 'default';
    return 'default';
};

export const getConfiguredChannels = (record: AppVersionUpdateItem) => {
    const rows: Array<{ key: ChannelKey; version?: string | null }> = [];
    if (record.ios_version_name) rows.push({ key: 'ios', version: record.ios_version_name });
    if (record.android_version_name) rows.push({ key: 'android', version: record.android_version_name });
    if (record.apk_version_name) rows.push({ key: 'apk', version: record.apk_version_name });
    return rows;
};

export const localeLabel = (locale: I18nLocale) => locale.toUpperCase();
